import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AcademicYearState {
  selectedAcademicYearId: number | null;
  selectedAcademicYearName: string | null;
  defaultAcademicYearId: number | null;
  isViewingHistorical: boolean;

  setSelectedAcademicYear: (id: number, name: string) => void;
  /** Sets the default year. If the user was viewing the default (not a manual historical selection),
   *  also updates selectedAcademicYearId so create-forms always use the current active year. */
  setDefaultAcademicYear: (id: number, name?: string) => void;
  resetToDefault: () => void;
  clearSelection: () => void;
}

export const useAcademicYearStore = create<AcademicYearState>()(
  persist(
    (set, get) => ({
      selectedAcademicYearId: null,
      selectedAcademicYearName: null,
      defaultAcademicYearId: null,
      isViewingHistorical: false,

      setSelectedAcademicYear: (id: number, name: string) => {
        const { defaultAcademicYearId } = get();
        set({
          selectedAcademicYearId: id,
          selectedAcademicYearName: name,
          isViewingHistorical: defaultAcademicYearId !== null && id !== defaultAcademicYearId,
        });
      },

      setDefaultAcademicYear: (id: number, name?: string) => {
        const { selectedAcademicYearId, defaultAcademicYearId } = get();
        // If the user was on the default year (or nothing selected), auto-switch to the new default
        const wasOnDefault = !selectedAcademicYearId || selectedAcademicYearId === defaultAcademicYearId;
        set({
          defaultAcademicYearId: id,
          ...(wasOnDefault
            ? {
                selectedAcademicYearId: id,
                selectedAcademicYearName: name ?? null,
                isViewingHistorical: false,
              }
            : {
                isViewingHistorical: selectedAcademicYearId !== null && selectedAcademicYearId !== id,
              }),
        });
      },

      resetToDefault: () => {
        const { defaultAcademicYearId } = get();
        if (defaultAcademicYearId) {
          set({
            selectedAcademicYearId: defaultAcademicYearId,
            isViewingHistorical: false,
          });
        }
      },

      clearSelection: () => {
        set({
          selectedAcademicYearId: null,
          selectedAcademicYearName: null,
          defaultAcademicYearId: null,
          isViewingHistorical: false,
        });
      },
    }),
    {
      name: 'academic-year-storage',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
      partialize: (state) => ({
        selectedAcademicYearId: state.selectedAcademicYearId,
        selectedAcademicYearName: state.selectedAcademicYearName,
        defaultAcademicYearId: state.defaultAcademicYearId,
        isViewingHistorical: state.isViewingHistorical,
      }),
    }
  )
);
